import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import StatCard from '../../../shared/layout/cards/StatCard';
import StatsGrid from '../../../shared/layout/containers/StatsGrid';
import LoadingMessage from '../../../shared/layout/feedback/LoadingMessage';
import ErrorMessage from '../../../shared/layout/feedback/ErrorMessage';
import { SimpleSelect } from '../../../shared/layout/forms/AppSelect';
import { fetchOrgScannedBills, fetchOrgScannedBillStats } from '../../../services/Http/Api';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../../../context/CurrencyProvider';

const OrganizationScannedBills = () => {
    const { t } = useTranslation();
    const { formatResolvedAmount } = useCurrency();
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState(null);
    const [perPage, setPerPage] = useState(20);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalRows, setTotalRows] = useState(0);
    const [statusFilter, setStatusFilter] = useState('');
    const statusOptions = useMemo(() => ([
        { value: '', label: t('ScannedBills.filters.all') },
        { value: 'org_review', label: 'org_review' },
        { value: 'processed', label: 'processed' },
        { value: 'rejected', label: 'rejected' },
    ]), [t]);

    const loadStats = async () => {
        try {
            const data = await fetchOrgScannedBillStats();
            setStats(data);
        } catch (err) {
            // non-blocking
        }
    };

    const loadScans = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await fetchOrgScannedBills({
                per_page: perPage,
                page: currentPage,
                status: statusFilter || undefined,
            });
            const rowsData = data?.data || data || [];
            const meta = data?.meta;
            setRows(rowsData);
            setTotalRows(meta?.total ?? rowsData.length);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    }, [perPage, currentPage, statusFilter]);

    useEffect(() => {
        loadStats();
        loadScans();
    }, [loadScans]);

    const columns = useMemo(() => ([
        {
            name: t('ScannedBills.columns.createdAt'),
            selector: row => row.created_at,
            sortable: true,
            width: '180px',
        },
        {
            name: t('ScannedBills.columns.user'),
            selector: row => row.user?.email || '-',
            sortable: false,
            grow: 1.2,
        },
        {
            name: t('ScannedBills.columns.status'),
            selector: row => row.status,
            sortable: true,
            width: '160px',
        },
        {
            name: t('ScannedBills.columns.total'),
            selector: row => row.total_amount ? formatResolvedAmount({
                eurAmount: row.total_amount,
                localAmount: row.total_amount_local,
                localCurrencyId: row.currency_id,
            }) : '-',
            sortable: false,
            width: '140px',
        },
        {
            name: t('ScannedBills.columns.actions'),
            cell: row => (
                <button
                    type="button"
                    className="btn btn-outline"
                    onClick={() => navigate(`/scanned-bills/${row.id}`)}
                >
                    {t('ScannedBills.view')}
                </button>
            ),
            width: '140px',
        },
    ]), [navigate, t, formatResolvedAmount]);

    return (
        <PageContainer>
            <PageHeader>
                <PageTitle>{t('ScannedBills.orgTitle')}</PageTitle>
            </PageHeader>
            <PageContent>
                {stats && (
                    <StatsGrid>
                        <StatCard title={t('ScannedBills.stats.total')} value={stats.total ?? 0} />
                        <StatCard title={t('ScannedBills.stats.pending')} value={stats.pending ?? 0} />
                        <StatCard title={t('ScannedBills.stats.processed')} value={stats.processed ?? 0} />
                        <StatCard title={t('ScannedBills.stats.rejected')} value={stats.rejected ?? 0} />
                    </StatsGrid>
                )}

                <div style={{ marginBottom: 16 }}>
                    <label htmlFor="scan-status-filter">
                        {t('ScannedBills.filters.status')}
                    </label>
                    <SimpleSelect
                        value={statusFilter}
                        onChange={(event, nextValue) => setStatusFilter(nextValue ?? event?.target?.value ?? '')}
                        options={statusOptions}
                        inputId="scan-status-filter"
                        style={{ width: 220, marginLeft: 8 }}
                    />
                </div>

                {loading ? (
                    <LoadingMessage>{t('ScannedBills.loading')}</LoadingMessage>
                ) : error ? (
                    <ErrorMessage>{t('ScannedBills.error')}</ErrorMessage>
                ) : (
                    <DataTable
                        columns={columns}
                        data={rows}
                        pagination
                        paginationServer
                        paginationTotalRows={totalRows}
                        onChangeRowsPerPage={setPerPage}
                        onChangePage={setCurrentPage}
                    />
                )}
            </PageContent>
        </PageContainer>
    );
};

export default OrganizationScannedBills;
